import React, { useCallback, useMemo, useRef } from 'react';

import { useReactiveVar } from '@apollo/client/react';
import { Typography } from '@mui/material';
import { useRichTreeViewProApiRef } from '@mui/x-tree-view-pro';
import { RichTreeViewPro } from '@mui/x-tree-view-pro/RichTreeViewPro';
import { CenteredLoader } from 'src/components/generic';
import type { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import {
  AlineTreeItem,
  folderIconWidthPx,
  FolderPaginationContext,
  labelGapPx,
} from './AlineTreeItem';
import { selectionStateVar, setSelectionState } from './FileStorageSyncDialog';
import { ResourceType, useFetchFolderChildren, useInitialTreeView } from './useFolderSyncJobs';
import type { TreeNode } from './useFolderSyncJobs';

const useStyles = makeStyles()(theme => ({
  loader: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    justifyContent: 'center',
    width: '100%',
  },
  root: {
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 8,
    display: 'flex',
    flex: 1,
    maxHeight: 400,
    minHeight: 400,
    overflow: 'auto',
    padding: theme.spacing(0.5, 0),
  },
  treeView: {
    width: '100%',
  },
}));

/**
 * IMPORTANT: All props passed to RichTreeViewPro MUST be referentially stable (hoisted
 * constants, useMemo, or useCallback).
 * Bug submitted: by Aline here https://github.com/mui/mui-x/issues/21451
 */
const TREE_VIEW_SLOTS = { item: AlineTreeItem } as const;
const SELECTION_PROPAGATION = { descendants: true, parents: false } as const;

function getChildrenCount(item: TreeNode) {
  return item.childrenCount ?? 0;
}

export function IntegrationDriveTreeView({
  folderSyncFolderIds,
  integration,
}: {
  folderSyncFolderIds: string[];
  integration: Apollo.FileStorageIntegration;
}): JSX.Element | null {
  const { classes } = useStyles();
  const apiRef = useRichTreeViewProApiRef();
  const scrollRootRef = useRef<HTMLDivElement>(null);
  const { selectedIds } = useReactiveVar(selectionStateVar);
  const { drivesLoading, rootItems } = useInitialTreeView(integration);
  const { fetchData, fetchMoreChildren, loadingMoreParentIds, paginationByParent } =
    useFetchFolderChildren(integration);

  const getTreeItems = useCallback(
    async (parentId?: string) => {
      if (!parentId) return [];

      const { treeMap } = selectionStateVar();
      const node = treeMap.get(parentId);
      return fetchData(parentId, node?.type === ResourceType.Drive ? 'drive' : 'folder');
    },
    [fetchData]
  );

  const dataSource = useMemo(() => ({ getChildrenCount, getTreeItems }), [getTreeItems]);

  const isItemDisabled = useCallback(
    (item: TreeNode) => {
      const isFile = item.type === 'file';
      const isAlreadySyncing = folderSyncFolderIds.includes(item.id);
      return isAlreadySyncing || isFile;
    },
    [folderSyncFolderIds]
  );

  const selectedItemsArray = useMemo(() => Array.from(selectedIds), [selectedIds]);

  const handleSelectedItemsChange = useCallback(
    (_: unknown, newSelectedIds: string[]) => {
      setSelectionState({
        selectedIds: new Set([...newSelectedIds, ...folderSyncFolderIds]),
      });
    },
    [folderSyncFolderIds]
  );

  const handleFetchMore = useCallback(
    async (parentId: string) => {
      const updatedParentId = await fetchMoreChildren(parentId);
      if (!updatedParentId) return;

      await apiRef.current?.updateItemChildren?.(updatedParentId);

      if (selectionStateVar().selectedIds.has(updatedParentId)) {
        apiRef.current?.setItemSelection?.({
          itemId: updatedParentId,
          keepExistingSelection: true,
          shouldBeSelected: true,
        });
      }
    },
    [apiRef, fetchMoreChildren]
  );

  const paginationContextValue = useMemo(
    () => ({
      loadingMoreParentIds,
      onLoadMore: handleFetchMore,
      paginationByParent,
      scrollRootRef,
    }),
    [handleFetchMore, loadingMoreParentIds, paginationByParent]
  );

  if (drivesLoading)
    return (
      <div className={classes.root}>
        <CenteredLoader className={classes.loader}>
          <Typography variant="subtitle2">Loading drives...</Typography>
        </CenteredLoader>
      </div>
    );

  if (!rootItems.length)
    return (
      <div className={classes.root}>
        <div className={classes.loader}>
          <Typography variant="h2">No drives found.</Typography>
        </div>
      </div>
    );

  return (
    <div ref={scrollRootRef} className={classes.root}>
      <FolderPaginationContext.Provider value={paginationContextValue}>
        <RichTreeViewPro
          checkboxSelection
          multiSelect
          apiRef={apiRef}
          className={classes.treeView}
          dataSource={dataSource}
          isItemDisabled={isItemDisabled}
          itemChildrenIndentation={folderIconWidthPx + labelGapPx}
          items={rootItems}
          selectedItems={selectedItemsArray}
          selectionPropagation={SELECTION_PROPAGATION}
          slots={TREE_VIEW_SLOTS}
          onSelectedItemsChange={handleSelectedItemsChange}
        />
      </FolderPaginationContext.Provider>
    </div>
  );
}
